import dayjs from 'dayjs'
import customFieldsFromUrl from 'embed_v2/helpers/custom_fields_from_url'
import { formatTime } from 'embed_v2/helpers/timer'
import useCloudflareTurnstile from 'embed_v2/hooks/useCloudflareTurnstile'
import useGoogleRecaptcha from 'embed_v2/hooks/useGoogleRecaptcha'
import useRemainTimeWorker from 'embed_v2/hooks/useRemainTimeWorker'
import { ValidationError, validationErrors } from 'embed_v2/services/validation'
import { WIDGET_TYPES } from 'embed_v2/types/version'
import { useState, useEffect, useRef, useMemo, useCallback } from 'preact/hooks'
import qs from 'query-string'
import { log } from 'core/helpers'
import i18n from 'core/services/i18n'
import Secrets from 'Secrets'
import ItemApi from '../ItemApi'
import executeChallengeFor from '../services/challenge'
import customHtmlForms from '../services/custom_html_forms'
import visitor from '../services/visitor'
import WidgetContext from './Context'
import Column from './widgets/Column'
import Element from './widgets/Element'
import Embed from './widgets/Embed'
import Floater from './widgets/Floater'
import Modal from './widgets/Modal'
import RegistrationPage from './widgets/RegistrationPage'
import Row from './widgets/Row'
import Step from './widgets/Step'
import ThankYou from './widgets/ThankYou'
import type Session from 'embed_v2/types/session'
import type ThankyouData from 'embed_v2/types/thankyou_data'
import type Version from 'embed_v2/types/version'

const parseName = (name?: string | null) => {
  if (!name || name.length === 0) return {}

  const fullName = name.trim().split(' ')

  const firstName = fullName.length > 1 ? fullName.slice(0, -1).join(' ') : fullName[0]
  const lastName = fullName.length > 1 ? fullName.slice(-1)[0] : ''

  return { firstName, lastName }
}

export type Notification = {
  message?: string | null
  errors: Record<string | 'base' | 'session', string | null>
}

type CustomTrackingFields = {
  cmc_vid?: string
  gclid?: string
  fbclid?: string
  fbp?: string
  fbc?: string
  wbraid?: string
}

type SubmitResponse = {
  custom_fields: object
  email: string
  first_name: string
  last_name: string
  phone: string
}

type Props = {
  api: ItemApi
  version: Version
  thankYouData?: ThankyouData
}

type SessionDeadline = { scheduled_at: string; expires_at?: string }

const sessionDeadline = (s: SessionDeadline) => s.expires_at || s.scheduled_at

const Widget = (props: Props) => {
  const { api, version, thankYouData } = props
  log('api', api, api.item, version, props)
  const appRef = useRef<HTMLDivElement>(null)
  const { executeRecaptcha } = useGoogleRecaptcha(
    version.recaptcha_enabled,
    version.webinar.recaptcha_integration.site_key
  )
  const { cloudflareTurnstileRef, cloudflareTurnstileInteractive, executeCloudflareTurnstile } = useCloudflareTurnstile(
    version.cloudflare_turnstile_enabled,
    version.cloudflare_turnstile_integration?.site_key
  )
  const [sessions, setSessions] = useState(version.formatted_sessions)
  let defaultSession: Session | undefined
  if (version.widget_type === WIDGET_TYPES.THANK_YOU) {
    defaultSession = thankYouData?.session
  } else {
    ;[defaultSession] = sessions
  }
  const startStep = version.steps.find(s => s.slug === api.stepSlug)
  const [currentStep, setCurrentStep] = useState(startStep || version.steps[0])
  const [viewer, setViewer] = useState(api.predefinedViewer)
  const [session, setSession] = useState<Session | undefined>(defaultSession)
  const [notification, notify] = useState<Notification>({ message: null, errors: {} })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const remainTimeWorker = useRemainTimeWorker(formatTime(session?.scheduled_at), api.onTimerTick)
  const { utm_source, utm_term, utm_campaign, utm_medium, utm_content, source, gclid, fbclid, wbraid } = qs.parse(
    window.location.search
  )

  const urlCustomFields = useMemo(
    () => customFieldsFromUrl(version.webinar.custom_fields_from_url || [], window.location.search),
    [version.webinar.custom_fields_from_url]
  )

  const isInvalid = () => session?.scheduled_at && dayjs(sessionDeadline(session)).diff(dayjs().add(5, 'seconds')) < 0

  const redirect = (redirectUrl: string) => {
    window.location.assign(redirectUrl)
  }

  const redirectToJoinUrl = useCallback(() => {
    if (thankYouData?.join_url) {
      window.open(thankYouData.join_url, '_parent')
    }
  }, [thankYouData?.join_url])

  const trackHtmlForms = (webinarCid: string, isLead: boolean, payload: object) => {
    const htmlTrack = isLead ? customHtmlForms.trackLeads : customHtmlForms.trackRegistrants
    htmlTrack(webinarCid, payload)
  }

  const executeChallenge = () =>
    executeChallengeFor({
      recaptchaEnabled: version.recaptcha_enabled,
      cloudflareTurnstileEnabled: version.cloudflare_turnstile_enabled,
      executeRecaptcha,
      executeCloudflareTurnstile
    })

  const submit = (isLead: boolean): Promise<SubmitResponse> => {
    if (version.widget_type !== WIDGET_TYPES.THANK_YOU && isInvalid()) {
      if (!isLead) {
        const newDefaultSession = sessions.find(
          s => s.type === session?.type && s.scheduled_at !== session?.scheduled_at && isSessionValid(s)
        )

        if (newDefaultSession) {
          setSession(newDefaultSession)

          throw new ValidationError([
            {
              field: 'base',
              error: `The time you selected is no longer available. We have automatically moved you to session (${newDefaultSession.formatted_scheduled_at}). You still need to register in order to confirm this updated slot.`
            }
          ])
        }

        throw new ValidationError([
          {
            field: 'base',
            error: i18n.t('errors.session_unavailable')
          }
        ])
      }
    }

    return new Promise((resolve, reject) => {
      const { first_name, last_name, name, email, phone, ...custom_fields } = viewer

      const custom_tracking_fields: CustomTrackingFields = {}
      if (window.clickmagick_cmc) {
        custom_tracking_fields.cmc_vid = window.clickmagick_cmc.vid
      }
      const gclidValue = [gclid].flat()[0]
      if (gclidValue) {
        custom_tracking_fields.gclid = gclidValue
      }

      const fbclidValue = [fbclid].flat()[0]
      if (fbclidValue) {
        custom_tracking_fields.fbclid = fbclidValue
      }

      const fbpValue = visitor.fbp
      if (fbpValue) {
        custom_tracking_fields.fbp = fbpValue
      }

      const fbcValue = visitor.fbc
      if (fbcValue) {
        custom_tracking_fields.fbc = fbcValue
      }

      const wbraidValue = [wbraid].flat()[0]
      if (wbraidValue) {
        custom_tracking_fields.wbraid = wbraidValue
      }

      const parsedName = parseName(name)

      const payload = {
        token: visitor.token,
        version_id: version.id,
        viewer: {
          webinar_session_id: session?.id,
          scheduled_at: session?.scheduled_at,
          sent_at: dayjs(),
          is_valid: dayjs(session?.scheduled_at).diff(dayjs().add(5, 'seconds')),
          time_zone: api.timeZone,
          email,
          phone,
          lead: isLead,
          utm_source,
          utm_term,
          utm_campaign,
          utm_medium,
          utm_content,
          registration_source_widget_type: version.widget_type,
          registration_source_widget_name: `${version.widget.name} ${version.name}`,
          widget_id: version.widget_id,
          widget_version_id: version.id,
          source: source || api.source,
          first_name: parsedName.firstName || first_name,
          last_name: parsedName.lastName || last_name,
          custom_fields: { ...urlCustomFields, ...custom_fields },
          custom_tracking_fields,
          embed_version_id: api.embedVersion?.id
        }
      }

      executeChallenge()
        .then(challenge => {
          api.subscribe(
            { ...payload, ...challenge },
            response => {
              setIsSubmitting(false)
              trackHtmlForms(version.webinar.cid, isLead, response)

              if (response.cid) {
                api.setSequenceCid(response.cid)
              }

              if (!isLead && response.redirect_url?.length) {
                redirect(response.redirect_url)
              } else {
                resolve({
                  custom_fields: response.custom_fields,
                  email: response.email,
                  first_name: response.first_name,
                  last_name: response.last_name,
                  phone: response.phone
                })
              }
            },
            response => {
              if (!isLead) {
                notify(prev => ({ ...prev, errors: { base: response.errors } }))
              }
              setIsSubmitting(false)
            }
          )
        })
        .catch(reject)
    })
  }

  const currentStepIndex = version.steps.findIndex(s => s.id === currentStep.id)
  const newPrevStep = version.steps[currentStepIndex - 1]
  const newNextStep = version.steps[currentStepIndex + 1]
  const canNextStep = !!newNextStep || version.widget_type === WIDGET_TYPES.THANK_YOU

  const prevStep = () => {
    log('prevStep', isSubmitting, newPrevStep, currentStep, version.steps)
    if (isSubmitting) return

    if (newPrevStep) {
      setCurrentStep(newPrevStep)
    }
  }

  const nextStep = () => {
    if (isSubmitting) return

    if (version.widget_type === WIDGET_TYPES.THANK_YOU) {
      redirectToJoinUrl()
    } else {
      api.validation
        .validate(currentStep.id, { ...viewer, date_time: session })
        .then(() => {
          if (newNextStep) {
            if (newNextStep.success) {
              setIsSubmitting(true)
              return submit(false).then(response => {
                setCurrentStep(newNextStep)
                props.api.onSubmit(response)
              })
            } else {
              setCurrentStep(newNextStep)

              if (viewer.email?.length && !isInvalid()) {
                submit(true)
                  .then(props.api.onLeadSubmit)
                  .catch(() => undefined)
              }
            }
          } else {
            setIsSubmitting(true)
            return submit(false).then(props.api.onSubmit)
          }
        })
        .catch(err => {
          log(err, err.data)
          notify(prev => ({ ...prev, errors: validationErrors(err) }))
          setIsSubmitting(false)
        })
    }
  }

  const setViewerField = (field: any, value: any) => {
    setViewer(prev => ({ ...prev, [field]: value }))
    notify(prev => ({ ...prev, errors: { ...prev.errors, [field]: null } }))
  }

  const updateViewer = () => {
    if (!thankYouData) return

    const payload = {
      cid: thankYouData.cid,
      phone: viewer.phone,
      time_zone: api.timeZone
    }

    return api.updateViewer(payload)
  }

  useEffect(() => {
    window.logDifference('Widget useEffect 1', api.uniqId)
    if (appRef.current) {
      api.node = appRef.current
    }

    props.api.on('setField', (field, value) => {
      setViewerField(field, value)
    })

    props.api.onReady()

    window.logDifference('Widget useEffect 1 end', api.uniqId)
  }, [api.uniqId])

  useEffect(() => {
    if (!remainTimeWorker) return
    if (version.widget_type !== WIDGET_TYPES.THANK_YOU) return

    const onTimeFn = (e: MessageEvent) => {
      if (e.data <= 0) {
        redirectToJoinUrl()
      }
    }
    remainTimeWorker.addEventListener('message', onTimeFn)

    return () => {
      remainTimeWorker?.removeEventListener('message', onTimeFn)
    }
  }, [remainTimeWorker, redirectToJoinUrl])

  const isSessionValid = (s: SessionDeadline) => !s.scheduled_at || dayjs(sessionDeadline(s)).diff(dayjs()) > 0

  const onSessionChange = (val: Session) => {
    if (version.widget_type !== WIDGET_TYPES.THANK_YOU) {
      setSession(val)
    }

    notify(prev => ({ ...prev, errors: { ...prev.errors, session: null } }))
  }

  const unsafeTagRegistrant = (tag: string) => {
    const notifyError = () => {
      notify({
        errors: {
          base: i18n.t('errors.challenge_email_mismatch')
        }
      })
    }

    fetch(`${Secrets.meetingHost}/embed/v2/viewers/tag_registrant`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        version_id: version.id,
        email: viewer.email,
        tag
      })
    })
      .then(response => response.json())
      .then(response => {
        if (response.errors) {
          notifyError()
        }

        if (response.thank_you_page_link?.length) {
          redirect(response.thank_you_page_link)
        }
      })
      .catch(() => {
        notifyError()
      })
  }

  const onCloseNotifications = () => {
    notify(prev => ({ ...prev, errors: { ...prev.errors, base: null } }))
  }

  const renderSteps = useMemo(() => {
    window.logDifference('Widget renderSteps', version.id)

    return (
      <div className={`wf_layout wf_${version.widget_type} wf_reset`}>
        {notification.errors.base && (
          <div className="wf_reset wf_layout_error">
            {notification.errors.base}
            <div className="wf_reset wf_layout_error_close" onClick={onCloseNotifications}>
              &times;
            </div>
          </div>
        )}
        {version.steps.map(step => (
          <Step id={step.id} key={step.id} isActive={currentStep.id === step.id}>
            {step.rows.map(row => (
              <Row id={row.id} key={row.id} title={row.title} options={row.options} widgetType={version.widget_type}>
                {row.columns.map(column => (
                  <Column
                    id={column.id}
                    width={column.width}
                    title={column.title}
                    options={column.options}
                    key={column.id}
                    columnGap={row.options.column_gap ?? 0}
                    columnsCount={row.columns.length}
                  >
                    {column.elements.map(element => (
                      <Element key={element.id} element={element} />
                    ))}
                  </Column>
                ))}
              </Row>
            ))}
          </Step>
        ))}
      </div>
    )
  }, [api.uniqId, currentStep.id, notification.errors.base])

  const renderWidget = () => {
    switch (version.widget_type) {
      case WIDGET_TYPES.FLOATER:
        return (
          <Floater isOpen={version.options.layout.open_by_default} handlerImage={version.options.handler.image_url}>
            {renderSteps}
          </Floater>
        )
      case WIDGET_TYPES.EMBED:
        return <Embed>{renderSteps}</Embed>
      case WIDGET_TYPES.MODAL:
        return <Modal api={api}>{renderSteps}</Modal>
      case WIDGET_TYPES.THANK_YOU:
        return <ThankYou>{renderSteps}</ThankYou>
      case WIDGET_TYPES.REGISTRATION_PAGE:
        return <RegistrationPage>{renderSteps}</RegistrationPage>
      default:
        return renderSteps
    }
  }

  log('props', props, appRef.current, session, viewer)

  if (!version) return null

  const context = {
    currentStep,
    version,
    sessions,
    session,
    viewer,
    prevStep,
    nextStep,
    setSessions,
    onSessionChange,
    redirectToJoinUrl,
    isSessionValid,
    setViewerField,
    notification,
    thankYouData,
    isSubmitting,
    api,
    newPrevStep,
    newNextStep,
    canNextStep,
    updateViewer,
    remainTimeWorker,
    unsafeTagRegistrant,
    cloudflareTurnstileRef,
    cloudflareTurnstileInteractive
  }

  return (
    <div id={`wf_${api.uniqId}`} ref={appRef}>
      <WidgetContext.Provider value={context}>{renderWidget()}</WidgetContext.Provider>
    </div>
  )
}

export default Widget
